import {
  Color,
  Mesh,
  Vector3,
  MeshStandardMaterial,
  Texture,
  type PerspectiveCamera,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import { METERS_PER_TEXEL, OCEAN, WORLD } from '../config';
import { buildOceanDisc } from './oceanGeometry';
import { waveSpectrumGLSL } from './waveField';

/**
 * Moře s Gerstnerovými vlnami.
 *
 * Základ je model z GPU Gems 1, kap. 1: každá vlna posouvá vrchol nejen
 * nahoru, ale i vodorovně proti směru šíření. Tím vznikne ostrý hřeben
 * a široká proláklina — přesně to, co odlišuje vodu od rozvlněné deky.
 * Normála se počítá analyticky ze stejných členů, takže osvětlení sedí
 * na geometrii přesně a nepotřebuje žádné finite differences.
 *
 * Shader navíc zná dno: vzorkuje **stejnou výškovou mapu jako terén**
 * a z hloubky odvozuje
 *   - útlum vln na mělčině (shoaling — vlny se u břehu srovnají),
 *   - Beer–Lambertovu absorpci barvy s hloubkou,
 *   - průhlednost a příboj u pobřeží.
 *
 * Pěna na hřebenech vychází z Jacobiánu Gerstnerova posunutí: kde se
 * povrch stlačuje sám do sebe, tam se vlna láme.
 */
export class Water {
  readonly mesh: Mesh;
  private readonly uniforms = {
    uTime: { value: 0 },
    uSeaLevel: { value: WORLD.seaLevel },
    uShallow: { value: new Color(0.10, 0.32, 0.34) },
    uDeep: { value: new Color(0.008, 0.055, 0.085) },
    uFoam: { value: new Color(0.92, 0.96, 0.97) },
    uWaveAmplitude: { value: OCEAN.amplitude },
    uChoppiness: { value: OCEAN.choppiness },
    uAbsorption: { value: OCEAN.absorption },
    uFoamCrest: { value: OCEAN.foamCrest },
    uSunDir: { value: new Vector3(0, 1, 0) },
    uSunColor: { value: new Color(1.0, 0.94, 0.82) },
  };

  constructor(heightMap: Texture, setupShadows?: (m: MeshStandardMaterial) => void) {
    const geometry = buildOceanDisc(OCEAN.rings, OCEAN.segments, OCEAN.innerRadius, OCEAN.outerRadius);

    const material = new MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.04,
      metalness: 0.0,
      transparent: true,
      depthWrite: false,
    });

    // CSM musí materiál zpracovat jako první — přepisuje onBeforeCompile.
    setupShadows?.(material);
    const previous = material.onBeforeCompile;
    const uniforms = this.uniforms;

    material.onBeforeCompile = function (shader: WebGLProgramParametersWithUniforms, renderer) {
      previous.call(material, shader, renderer);

      Object.assign(shader.uniforms, uniforms, {
        uHeightMap: { value: heightMap },
        uHeightRes: { value: WORLD.heightRes },
        uWorldSize: { value: WORLD.size },
        uTexel: { value: METERS_PER_TEXEL },
      });

      shader.vertexShader = WAVE_COMMON + VERT_HEAD + shader.vertexShader;
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <beginnormal_vertex>',
          `#include <beginnormal_vertex>
          vec3 wBase = (modelMatrix * vec4(position, 1.0)).xyz;
          float wDepthBase = uSeaLevel - wTerrainHeight(wBase.xz);
          // Útlum na mělčině: pod hloubkou srovnatelnou s amplitudou už
          // vlna nemá kam kmitat a láme se.
          float wShoal = smoothstep(0.0, 9.0, wDepthBase);
          float wVertDist = length(wBase - cameraPosition);
          WaveResult wave = wGerstner(wBase.xz, uTime, wShoal, wVertDist, 1.0);
          objectNormal = wave.normal;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          transformed += wave.offset;
          vWorldPos = wBase + wave.offset;
          vShoal = wShoal;
          vDepth = uSeaLevel - wTerrainHeight(vWorldPos.xz);`,
        );

      shader.fragmentShader = WAVE_COMMON + FRAG_HEAD + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <map_fragment>', FRAG_COLOR)
        .replace(
          '#include <roughnessmap_fragment>',
          `#include <roughnessmap_fragment>
          // Pěna je matná, klidná hladina je téměř zrcadlo.
          // Zrcadlove hladka voda dela z kazde vlnky ostrou jiskru a ta se
          // pri jednom vzorku na pixel rozpada na pravidelny vzor. Sirsi
          // lalok je klidnejsi a realnejsi.
          roughnessFactor = mix(0.075, 0.55, wFoamAmount);`,
        )
        .replace(
          '#include <normal_fragment_begin>',
          `#include <normal_fragment_begin>
          normal = normalize((viewMatrix * vec4(wSurfaceNormal, 0.0)).xyz);`,
        )
        .replace('#include <opaque_fragment>', FRAG_GLITTER + '#include <opaque_fragment>');
    };

    this.mesh = new Mesh(geometry, material);
    this.mesh.name = 'water';
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = false;
    this.mesh.castShadow = false;
    this.mesh.renderOrder = 2;
  }

  update(elapsed: number, camera: PerspectiveCamera, sunDirection?: Vector3): void {
    this.uniforms.uTime.value = elapsed;
    // Směr ke slunci (sunDirection míří od slunce dolů, třpyt potřebuje opačný).
    if (sunDirection) this.uniforms.uSunDir.value.copy(sunDirection).negate().normalize();
    // Disk jede s kamerou; vlny se počítají ze světových souřadnic,
    // takže hladina zůstane ukotvená a nikde se neposouvá.
    this.mesh.position.set(camera.position.x, WORLD.seaLevel, camera.position.z);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshStandardMaterial).dispose();
  }
}
/** Sdílené mezi vertex a fragment částí. */
const WAVE_COMMON = /* glsl */ `
uniform float uTime;
uniform float uSeaLevel;
uniform float uWaveAmplitude;
uniform float uChoppiness;
uniform sampler2D uHeightMap;
uniform float uHeightRes;
uniform float uWorldSize;
uniform float uTexel;

float wTerrainHeight(vec2 wxz) {
  vec2 t = (wxz + uWorldSize * 0.5) / uTexel;
  vec2 f = fract(t);
  ivec2 i0 = ivec2(floor(t));
  ivec2 m = ivec2(int(uHeightRes) - 1);
  float h00 = texelFetch(uHeightMap, clamp(i0, ivec2(0), m), 0).r;
  float h10 = texelFetch(uHeightMap, clamp(i0 + ivec2(1, 0), ivec2(0), m), 0).r;
  float h01 = texelFetch(uHeightMap, clamp(i0 + ivec2(0, 1), ivec2(0), m), 0).r;
  float h11 = texelFetch(uHeightMap, clamp(i0 + ivec2(1, 1), ivec2(0), m), 0).r;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}

float wHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float wNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(i), wHash(i + vec2(1.0, 0.0)), u.x),
             mix(wHash(i + vec2(0.0, 1.0)), wHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float wFbm(vec2 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { s += a * wNoise(p); p *= 2.09; a *= 0.5; }
  return s;
}

struct WaveResult {
  vec3 offset;
  vec3 normal;
  float jacobian;
};

// smer.xy, vlnova delka, relativni amplituda
//
// Smery jsou zamerne rozprostrene do siroka. Kdyz jsou vlny skoro
// rovnobezne, hladina vypada jako vlnity plech - realne more ma
// smerove spektrum rozprostrene kolem smeru vetru.
// Vlnove delky jsou kratsi nez u sireho more. Sedmdesatimetrova vlna je
// oceansky swell - proti postave vysoke 1.8 m vypada jako pohybujici se
// kopec a hladina pusobi jako obri deka. U pobrezi patri kratsi spektrum.
${waveSpectrumGLSL()}

/**
 * Gerstnerova vlna (GPU Gems 1, kap. 1).
 *
 * geoFade tlumi posunuti vrcholu, shadeFade tlumi prispevek do normaly.
 * Obe jsou zavisle na vzdalenosti a na vlnove delce: vlnu kratsi nez
 * rozestup vrcholu nema smysl posouvat (vznikly by facety) a vlnu kratsi
 * nez pixel nema smysl stinovat (aliasovala by).
 */
WaveResult wGerstner(vec2 p, float t, float shoal, float dist, float geoScale) {
  WaveResult r;
  r.offset = vec3(0.0);
  vec3 n = vec3(0.0, 1.0, 0.0);
  float jacobian = 1.0;



  // Vlnove skupiny.
  //
  // Soucet sesti sinusovek jede vsude stejne silne, takze hladina vypada
  // jako vlnity plech - jeden pravidelny vzor pres cely obzor. Skutecne
  // more jde v skupinach: nekolik vyssich vln, pak klidnejsi pasmo. Pomalu
  // se posouvajici sum tady moduluje amplitudu podle mista, cimz se ta
  // pravidelnost rozbije, aniz by to stalo jediny dalsi sinus.
  float wGroup = 0.45 + 1.05 * wFbm(p * 0.0055 + vec2(t * 0.013, -t * 0.009));

  for (int i = 0; i < W_COUNT; i++) {
    vec2 dir = normalize(W_SPEC[i].xy);
    float wavelength = W_SPEC[i].z;
    // Kratke vlny se skupinami moduluji min - drobne vlnky jsou vsude.
    float groupMix = mix(1.0, wGroup, clamp(wavelength / 20.0, 0.25, 1.0));
    float amplitude = W_SPEC[i].w * uWaveAmplitude * shoal * groupMix;

    float k = 6.28318530718 / wavelength;
    // Disperzni vztah hlubinne vlny: c = sqrt(g/k). Kratsi vlny jedou
    // pomaleji, proto hladina nevypada jako jeden posuvny vzor.
    float speed = sqrt(9.81 / k);
    float phase = k * dot(dir, p) + t * speed * k + W_PHASE[i];
    // Ohyb hrebene: fazovy posun podel kolmice k smeru sireni. Bez nej
    // jsou hrebeny dokonale rovne primky, coz zadne more nema. Frekvence
    // ohybu je pro kazdou vlnu jina, jinak se ohyby srovnaji do zakrytu.
    vec2 perp = vec2(-dir.y, dir.x);
    float bend = 0.013 + 0.011 * float(i);
    phase += 1.6 * sin(dot(perp, p) * bend + t * 0.11 + W_PHASE[i] * 2.0);

    float c = cos(phase);
    float s = sin(phase);
    float q = min(uChoppiness / (k * amplitude * 5.0 + 1e-5), 1.0);

    float shadeFade = 1.0 - smoothstep(wavelength * 22.0, wavelength * 45.0, dist);
    float geoFade = geoScale * (1.0 - smoothstep(wavelength * 4.0, wavelength * 8.0, dist));

    float aGeo = amplitude * geoFade;
    r.offset.x += q * aGeo * dir.x * c;
    r.offset.z += q * aGeo * dir.y * c;
    r.offset.y += aGeo * s;

    float wa = k * amplitude * shadeFade;
    n.x -= dir.x * wa * c;
    n.z -= dir.y * wa * c;
    n.y -= q * wa * s;
    jacobian -= q * wa * s;
  }

  r.normal = normalize(n);
  r.jacobian = jacobian;
  return r;
}
`;



/**
 * Sluneční třpyt.
 *
 * Standardní materiál dá ze směrového světla jeden ostrý bod. Skutečná voda
 * ale rozhodí odraz slunce do dlouhé jiskřící cesty, protože každá drobná
 * vlnka natočí normálu jinam. Skládá se proto ze dvou laloků: úzkého na
 * samotný odlesk a širokého na tu cestu k obzoru. Bez toho vypadá hladina
 * jako obarvené sklo.
 */
const FRAG_GLITTER = /* glsl */ `
{
  vec3 wView = normalize(cameraPosition - vWorldPos);
  vec3 wHalf = normalize(wView + uSunDir);
  float wNdH = max(dot(wSurfaceNormal, wHalf), 0.0);
  float wSun = max(uSunDir.y, 0.0);
  float wGlint = pow(wNdH, 900.0) * 3.0 + pow(wNdH, 60.0) * 0.35;
  // Pěna je matná — ta se netřpytí.
  outgoingLight += uSunColor * wGlint * wSun * (1.0 - wFoamAmount) * 1.4;
}
`;

const VERT_HEAD = /* glsl */ `
varying vec3 vWorldPos;
varying float vDepth;
varying float vShoal;
`;
const FRAG_HEAD = /* glsl */ `
uniform vec3 uShallow;
uniform vec3 uDeep;
uniform vec3 uFoam;
uniform float uAbsorption;
uniform float uFoamCrest;
// Bez těchhle dvou deklarací se fragment shader nezkompiluje a voda se
// přestane kreslit úplně — předat uniform do shader.uniforms nestačí.
uniform vec3 uSunDir;
uniform vec3 uSunColor;

varying vec3 vWorldPos;
varying float vDepth;
varying float vShoal;

`;

const FRAG_COLOR = /* glsl */ `
float wDist = length(vWorldPos - cameraPosition);

// Normala se pocita analyticky tady, ne interpolaci z vrcholu. Disk ma
// u obzoru trojuhelniky stovky metru velke a interpolovana normala z nich
// delala obri lesklé facety. Per-pixel vypocet je stoji par sinu navic
// a hladina zustane hladka az k obzoru. geoScale = 0: posunuti uz
// probehlo ve vertex shaderu, tady nas zajima jen tvar.
WaveResult wSurf = wGerstner(vWorldPos.xz, uTime, vShoal, wDist, 0.0);
vec3 wSurfaceNormal = wSurf.normal;
// Drobne vlnky jen do blizka.
//
// Sahaly do 420 m a s ostrym odleskem z toho vznikaly pravidelne pasy
// kopirujici linie stejne vzdalenosti od kamery - typicky aliasing
// vysokofrekvencni normaly. Jeden vzorek na pixel takovou frekvenci
// neuhlida a hladina vypadala jako vlnity plech az k obzoru.
float wRippleFade = 1.0 - smoothstep(12.0, 110.0, wDist);
if (wRippleFade > 0.002) {
  vec2 q = vWorldPos.xz * 0.55 + vec2(uTime * 0.22, uTime * 0.14);
  float r0 = wFbm(q);
  vec2 grad = vec2(wFbm(q + vec2(0.35, 0.0)) - r0, wFbm(q + vec2(0.0, 0.35)) - r0);
  wSurfaceNormal = normalize(wSurfaceNormal + vec3(-grad.x, 0.0, -grad.y) * 0.55 * wRippleFade);
}

// Beer-Lambert: svetlo se ve vode pohlcuje exponencialne s hloubkou,
// a pro kazdou slozku jinak - proto je melcina tyrkysova a hloubka temna.
float wDepthClamped = max(vDepth, 0.0);
vec3 wTransmission = exp(-uAbsorption * wDepthClamped * vec3(0.42, 0.16, 0.11));
vec3 wBody = mix(uDeep, uShallow, wTransmission.g);

// Pena ma dva zdroje: lamajici se hrebeny (Jacobian pod jednickou znamena,
// ze se povrch stlacuje sam do sebe) a pribojova cara na melcine.
// Pena se objevi az tam, kde se povrch opravdu prekloni. Prah musi byt
// hluboko pod jednickou, jinak "sumi" pena po cele hladine.
float wCrestRaw = smoothstep(uFoamCrest, uFoamCrest - 0.16, wSurf.jacobian);
// Rozbiti sumem, aby pena nekopirovala geometrii vlny jako sablona.
float wCrestNoise = wFbm(vWorldPos.xz * 0.9 + vec2(uTime * 0.25, -uTime * 0.18));
float wCrest = wCrestRaw * smoothstep(0.42, 0.80, wCrestNoise);
// Priboj se meri v METRECH OD CARY BREHU, ne podle hloubky.
//
// Hloubkovy prah je totiz zradny: sirka pasu pak zavisi na sklonu dna.
// Na mirne plazi znamena "mene nez metr vody" klidne padesat metru, a z
// priboje se stane bila mlha pres pul zalivu. Vzdalenost se spocita ze
// sklonu dna, takze pas ma vsude stejnou sirku bez ohledu na profil.
vec2 wE = vec2(uTexel, 0.0);
float wHx = wTerrainHeight(vWorldPos.xz + wE.xy) - wTerrainHeight(vWorldPos.xz - wE.xy);
float wHz = wTerrainHeight(vWorldPos.xz + wE.yx) - wTerrainHeight(vWorldPos.xz - wE.yx);
float wSlope = length(vec2(wHx, wHz)) / (2.0 * uTexel);
float wShoreDist = wDepthClamped / max(wSlope, 0.006);

// Priboj dyse: pas se s vlnami vysouva na breh a zase stahuje. Faze je
// posunuta sumem podel pobrezi, aby cela zatoka nepulzovala naraz.
float wSwashPhase = uTime * 0.5 + wFbm(vWorldPos.xz * 0.012) * 6.28318;
// Vetsi rozkmit: priboj ma na breh viditelne vybihat a zase se stahovat,
// ne jen dychat o par centimetru. Rozdil mezi nejzazsim vybehem a stazenim
// je tak zhruba trojnasobek.
float wSwash = 6.0 + 4.6 * sin(wSwashPhase);
float wShore = 1.0 - smoothstep(wSwash * 0.25, wSwash, wShoreDist);
wShore *= smoothstep(-0.3, 0.25, wDepthClamped);
// Rozbiti, aby cara priboje nebyla hladky kruh kolem ostrova.
float wShoreNoise = wFbm(vWorldPos.xz * 0.28 + vec2(uTime * 0.09, -uTime * 0.05));
wShore *= smoothstep(0.20, 0.62, wShoreNoise);

float wFoamAmount = clamp(max(wCrest, wShore), 0.0, 1.0);
wFoamAmount *= 1.0 - smoothstep(350.0, 1100.0, wDist);

diffuseColor.rgb *= mix(wBody, uFoam, wFoamAmount);

// Pruhlednost u brehu - bez ni vznika ostra vystrizena hrana.
//
// Nabiha ale rychle a vysoko. Kdyz zustane hladina skoro pruhledna, je dno
// videt uplne ciste a bez zkresleni - voda pak pusobi jako sklenena placka
// polozena na pisku, protoze jedine, co ji prozrazuje, je barevny nadech.
// Uz od pul metru proto prevazuje barva vody a od nekolika metru dno mizi.
float wAlpha = smoothstep(0.0, 0.75, wDepthClamped) * mix(0.80, 0.99, smoothstep(0.4, 9.0, wDepthClamped));

// Fresnel drzi hladinu videt i tam, kde je pruhledna.
//
// Na melcine vychazela alfa skoro nulova, takze skrz vodu prosvitalo holé
// pisecne dno a hladina ztratila veskery lesk - vypadalo to jako sucha
// plaz, ne jako voda. Pritom prave pod ostrym uhlem voda odrazi nejvic:
// odrazivost roste k jednicce, jak se pohled blizi rovine hladiny. Alfa
// proto nesmi klesnout pod Fresneluv clen, jinak se s ni vynasobi i odlesk
// oblohy a slunce a povrch zmizi.
vec3 wViewDir = normalize(cameraPosition - vWorldPos);
float wFacing = clamp(dot(wViewDir, wSurfaceNormal), 0.0, 1.0);
float wFresnel = 0.02 + 0.98 * pow(1.0 - wFacing, 5.0);

diffuseColor.a = clamp(max(max(wAlpha, wFoamAmount * 0.80), wFresnel * 0.9), 0.0, 1.0);
`;
